/**
 * Dock — the ONE copilot bar (TASK-713).
 *
 * Mounted once in Layout on every authenticated route except the temporary
 * suppression list (unmigrated embedded copilots) and `/dashboard` (where the
 * canvas InputDock IS the dock placement — TASK-712). Scope chip derives from
 * the route (frontend mirror of route_classifier); replies render in the
 * TranscriptSheet; transport is the shared useAgenticChat over /ws/chat with a
 * per-scope entity-qualified store (TASK-710 identity decision).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import CreateArtifactDialog from '../documents/CreateArtifactDialog';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { clientService } from '../../services/clientService';
import { useAccount } from '../../contexts/AccountContext';
import { useDockStore } from '../../stores/dockStore';
import { getDockChatStore } from '../../stores/dockChatStores';
import { generateChatMessageId } from '../../stores/createChatStore';
import { useAgenticChat, type HardGateConfirm } from '../../hooks/useAgenticChat';
import { resolveDockScope, threadScope, isDockSuppressed } from './dockScope';
import { FEATURES } from '../../config/api-config';
import { ChatBubble } from './ChatBubble';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { ConnectionPill } from './ConnectionPill';
import { ToolProgress } from './ToolProgress';
import { useDockChunkHandlers } from './useDockChunkHandlers';
import { useDockSend } from './useDockSend';
import { TranscriptSheet } from './TranscriptSheet';
import { ScopeChip, WorkingStrip } from './ScopeChip';
import { DockMessage } from './DockMessage';
import { ConfirmDialog } from '../ui/confirm-dialog';

/** TASK-766: the chip's `aria-controls` target — one dock, one sheet. */
const TRANSCRIPT_SHEET_ID = 'dock-transcript-sheet';

export const Dock: React.FC = () => {
  const { isAuthenticated, hasPermission, account } = useAccount();
  const location = useLocation();

  if (!isAuthenticated || !hasPermission('chat:read') || isDockSuppressed(location.pathname)) {
    return null;
  }
  return <DockInner accountId={account?.id ?? 'unknown'} />;
};

const DockInner: React.FC<{ accountId: string }> = ({ accountId }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  // TASK-769: placement and thread are independent axes. The route says which
  // surface is on screen; the work says which conversation is in it.
  const placement = useMemo(() => resolveDockScope(location.pathname), [location.pathname]);
  const activeTaskThread = useDockStore((s) => s.activeTaskThread);
  const scope = useMemo(
    () => (FEATURES.ENABLE_TASK_THREADS ? threadScope(placement, activeTaskThread) : placement),
    [placement, activeTaskThread]
  );
  const useStore = useMemo(
    () => getDockChatStore(accountId, scope),
    [accountId, scope.scopeType, scope.entityId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const messages = useStore((s) => s.messages);
  const isStreaming = useStore((s) => s.isStreaming);
  const turn = useStore((s) => s.turn);
  const error = useStore((s) => s.error);

  const [sheetOpen, setSheetOpen] = useState(false);
  /**
   * TASK-797: the gate is held WITH the conversation it arrived on. `blocked`
   * waits on a human, so this is open for as long as someone deliberates —
   * long enough to switch scope, which would otherwise send the approval
   * against the new scope's `conversationId` and have the server answer "no
   * longer pending", silently not executing what the advisor just approved.
   */
  const [pendingConfirm, setPendingConfirm] = useState<
    (HardGateConfirm & { originConversationId: string | null }) | null
  >(null);
  const inputRef = useRef<ChatInputHandle>(null);

  // Client-name enrichment for the chip + entity envelope (cached; never guessed).
  const { data: client } = useQuery({
    queryKey: ['client', scope.entity?.id],
    queryFn: () => clientService.getClient(scope.entity!.id),
    enabled: scope.entity?.type === 'client',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const chipLabel =
    // TASK-769: a task thread NAMES itself. The advisor is never guessing which
    // conversation the chip opens — that is what makes the precedence rule safe
    // to explain, even though it is written so the ambiguous case cannot arise.
    scope.scopeType === 'task'
      ? `Task · ${scope.label}`
      : scope.entity?.type === 'client' && client?.name
        ? `${scope.scopeType === 'kyc' ? 'KYC' : 'Client'} · ${client.name}`
        : scope.entity?.type === 'meeting'
          ? `Brief · ${scope.entity.id.slice(0, 18)}`
          : scope.label;

  // Side channels belong to the PLACEMENT, not the thread: which chunks this
  // surface can render is a property of the screen, not of the conversation.
  // A task thread opened on meeting prep must not silently stop handling
  // brief_update — the placement still knows how to draw one.
  const isMeetingScope = placement.scopeType === 'meeting';
  // Still the KYC SURFACE's own question (its starter chips), which is a real property of the
  // screen. It is no longer what decides whether a proposal can be applied — see below.
  const isKycScope = placement.scopeType === 'kyc';
  // The placement's client, or null. Read off the entity envelope rather than
  // `placement.entityId`, which on a meeting placement is a MEETING id — harmless while the
  // proposal cards were KYC-only, a cross-entity write the moment they are not.
  const placementClientId =
    placement.entity?.type === 'client' ? placement.entity.id : null;

  // TASK-746: the one create dialog, reachable from the conversation surface.
  // On client routes the dock's resolved scope IS the client context chip.
  const [createOpen, setCreateOpen] = useState(false);
  const scopeClientId = scope.entity?.type === 'client' ? scope.entity.id : null;

  const store = useStore; // stable handle for callbacks

  // TASK-794: the chunk-handler map and its tool-progress state, extracted.
  const { handlers, ignored, toolEvents, setToolEvents } = useDockChunkHandlers({
    store,
    isMeetingScope,
    canApplyProposals: placementClientId !== null,
    onConfirm: (confirm) =>
      setPendingConfirm({
        ...confirm,
        originConversationId: store.getState().conversationId,
      }),
  });

  const {
    isConnected,
    isReconnecting,
    isStreaming: wsStreaming,
    streamingContent,
    sendMessage: wsSendMessage,
    sendConfirm: wsSendConfirm,
    connect,
  } = useAgenticChat({
    logLabel: `dock:${scope.scopeType}:${scope.entityId ?? 'global'}`,
    onMessageComplete: (content, convId) => {
      store.getState().addMessage({
        id: generateChatMessageId('dock'),
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
      });
      store.getState().commitPending();
      if (convId) store.getState().setConversationId(convId);
      store.getState().setTurn('done');
      setToolEvents([]);
    },
    onError: (err) => {
      store.getState().setError(err);
      // TASK-765: setError no longer stops the turn implicitly — an error ends
      // it, so say so. Without this the input stays locked forever.
      store.getState().setTurn('error');
      setToolEvents([]);
    },
    handlers,
    ignored,
  });

  /**
   * TASK-797: answering the gate is what LEAVES `blocked`, and the answer has to
   * be known to have gone out.
   *
   * Two things the state's new meaning made mandatory. `blocked` now locks the
   * input, so a silently-lost answer is a permanent lock rather than the old
   * harmless inconsistency — `sendConfirm` returns false when the socket is not
   * open, which is a real window here because this state waits on a person. And
   * once answered, the turn is no longer waiting on the advisor; leaving it in
   * `blocked` made the working strip say "Waiting for your confirmation…" while
   * the gated action was actually executing.
   *
   * The send path already treats a refused send this way (`useDockSend`); the
   * confirm path was the one that dropped the boolean on the floor.
   */
  const decideHardGate = (decision: 'confirm' | 'cancel') => {
    if (!pendingConfirm) return;
    const sent = wsSendConfirm(
      pendingConfirm.action_id,
      decision,
      pendingConfirm.originConversationId
    );
    setPendingConfirm(null);
    if (!sent) {
      store.getState().setError('That confirmation could not be sent — please try again.');
      store.getState().setTurn('error');
      return;
    }
    store.getState().setTurn('working');
  };

  /**
   * TASK-797: recover a turn stranded in `blocked` by an unmount.
   *
   * The dock unmounts on `/dashboard` and every workbench route, taking the
   * dialog's React state and the socket with it while the scope store — a
   * session-cached singleton — keeps `turn: 'blocked'`. Coming back, the advisor
   * would meet a locked input with no dialog and no chunk that could ever arrive,
   * fixable only by reloading. The stream that turn belonged to is gone, so the
   * honest resolution is to end it.
   */
  useEffect(() => {
    if (store.getState().turn === 'blocked') {
      store.getState().setError('That confirmation was interrupted — please ask again.');
      store.getState().setTurn('error');
    }
    // Mount only, per scope: a `blocked` turn reached here without its dialog.
  }, [store]);

  // TASK-792: `wsStreaming` FEEDS the machine instead of standing beside it.
  // Two booleans that could disagree is what let TASK-765 hide — the rail
  // guarded on the transport flag and degraded, the dock guarded on the store
  // flag and failed outright, from one root cause. The transport now reports
  // one transition (first token arrived) into the single turn state.
  useEffect(() => {
    if (!wsStreaming) return;
    const current = store.getState().turn;
    if (current === 'routing' || current === 'working') store.getState().setTurn('streaming');
  }, [wsStreaming, store]);


  useEffect(() => {
    connect();
  }, [connect]);

  // TASK-746: CreateArtifactDialog's "Generate from description" prefills the
  // dock input — the user reviews the instruction and sends it.
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string }>).detail;
      if (!detail?.text) return;
      setSheetOpen(true);
      setTimeout(() => inputRef.current?.setValue(detail.text as string), 0);
    };
    window.addEventListener('kielis:dock-prefill', onPrefill);
    return () => window.removeEventListener('kielis:dock-prefill', onPrefill);
  }, []);

  // `/` focuses the dock input everywhere (was: opened the floating chat).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          setSheetOpen(true);
          // Focus after the sheet mounts.
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // TASK-794: the send path — guard, display/payload split, context envelope and
  // the queued handoff from another surface — extracted as one unit.
  const send = useDockSend({
    store,
    placement,
    scope,
    clientName: client?.name,
    routeWithQuery: location.pathname + location.search,
    language: i18n.language,
    isStreaming,
    wsSendMessage,
    connect,
    onSend: () => setSheetOpen(true),
  });
  const closeTaskThread = useDockStore((s) => s.closeTaskThread);

  return (
    <>
      <TranscriptSheet
        id={TRANSCRIPT_SHEET_ID}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        scopeLabel={chipLabel}
      >
        {messages.length === 0 && !wsStreaming && (
          <div className="space-y-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              Ask questions about clients, portfolios, or get help — scoped to {chipLabel}.
            </p>
            {/* TASK-719: scope-aware starter chips — one send pipeline. */}
            <div className="flex flex-wrap justify-center gap-2">
              {(isKycScope
                ? [
                    'Which KYC sections are still incomplete?',
                    'Fill the form from the uploaded documents',
                    "Derive this client's DNA from the knowledge graph",
                  ]
                : isMeetingScope
                  ? [
                      'Update the brief with the latest portfolio changes',
                      'Propose an agenda for this meeting',
                      'Add a follow-up task for after the meeting',
                    ]
                  : [
                      'What needs my attention today?',
                      'Summarise this page for me',
                    ]
              ).map((starter) => (
                <button
                  key={starter}
                  type="button"
                  onClick={() => send(starter)}
                  className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  data-testid="dock-starter-chip"
                >
                  {starter}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <DockMessage
            key={m.id}
            message={m}
            onQuery={send}
            isMeetingScope={isMeetingScope}
            placementClientId={placementClientId}
          />
        ))}
        {toolEvents.length > 0 && <ToolProgress events={toolEvents} />}
        {wsStreaming && streamingContent && (
          <ChatBubble role="assistant" content={streamingContent} streaming />
        )}
        {isStreaming && !streamingContent && toolEvents.length === 0 && (
          <ChatBubble role="assistant" content="…" streaming />
        )}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </TranscriptSheet>

      {pendingConfirm && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) decideHardGate('cancel');
          }}
          title={pendingConfirm.summary || `Confirm ${pendingConfirm.tool_name}`}
          description={pendingConfirm.risk || 'This action needs your explicit confirmation.'}
          onConfirm={() => decideHardGate('confirm')}
        />
      )}

      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 px-4 py-2 backdrop-blur"
        data-testid="global-dock"
      >
        {/* TASK-766: a deliberate collapse is respected — the sheet is not
            reopened underneath the advisor. This strip carries the same
            information without taking the screen back, and names the work so
            "something is happening" becomes "this is happening". */}
        <WorkingStrip
          visible={!sheetOpen && isStreaming}
          label={
            // TASK-797: `blocked` outranks the running tool label — while the
            // hard-gate dialog is up, the turn is waiting on the ADVISOR, and
            // naming the tool would tell them work is proceeding when it is
            // theirs to release. Checked first for that reason.
            turn === 'blocked'
              ? 'Waiting for your confirmation…'
              : toolEvents.find((e) => e.status === 'running')?.label ??
                // TASK-792: routing is a distinct phase, so say so rather than
                // calling the whole pre-token window "working".
                (turn === 'routing' ? 'Choosing a specialist…' : `Working on ${chipLabel}…`)
          }
          onShowConversation={() => setSheetOpen(true)}
        />
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <ScopeChip
            label={chipLabel}
            sheetOpen={sheetOpen}
            onToggle={() => setSheetOpen((v) => !v)}
            live={isStreaming}
            messageCount={messages.length}
            controls={TRANSCRIPT_SHEET_ID}
            onLeaveThread={scope.scopeType === 'task' ? closeTaskThread : undefined}
          />
          <div className="mb-1 shrink-0">
            <ConnectionPill
              isConnected={isConnected}
              isReconnecting={isReconnecting}
              onReconnect={connect}
            />
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            title="Create a document, sheet or deck"
            data-testid="dock-open-create"
            className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <ChatInput
              ref={inputRef}
              onSend={send}
              disabled={isStreaming}
            />
          </div>
        </div>
      </div>
      <CreateArtifactDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        contextClientId={scopeClientId}
        contextClientName={scopeClientId ? (client?.name ?? null) : null}
      />
    </>
  );
};

export default Dock;
